import { appendDpopPublicKey } from '@unifyapps/network/dpop/appendDpopPublicKey';
import type { DpopPublicKey } from '@unifyapps/network/dpop/key';
import { withDpopLoginBinding } from '@unifyapps/network/dpop/loginBinding';
import { getDpopPublicKey } from '@unifyapps/network/dpop/store';
import type { ExecuteWorkflowNodeRequest } from '@unifyapps/network/generated/models/executeWorkflowNodeRequest';
import type { ExecuteWorkflowNodeResponse } from '@unifyapps/network/generated/models/executeWorkflowNodeResponse';
import { getExecuteWorkflowNodeQueryKey as originalGetExecuteWorkflowNodeQueryKey } from '@unifyapps/network/generated/workflow-rest-api/workflow-rest-api';
import { executeFetch } from '@unifyapps/network/utils/fetch';
import getDomain from '@unifyapps/network/utils/url/getDomain';
import type { JSONSchema7 } from 'json-schema';
import { omit } from 'lodash';
import { createMemo } from 'react-use'; // dont change/minimize this import, required full path for react-native
import { postAuthWorkflowNode } from '../../../../../utils/authWorkflowNode';
import { createSearchParams } from '../../../../../utils/createSearchParams';
import type { RAW_RESPONSE_KEY } from '../../../consts';
import type { DebugParams } from './types';

export enum ExecutePlatformByUAOperation {
  CreateConnectionWithInput = 'createConnectionWithInput',
}

export type ExecuteWorkflowNodeQueryKeyType =
  | 'offline'
  | 'sse'
  | 'formdata'
  | 'auth'
  | 'api'
  | 'api-update-password'
  | 'api-rungetschema'
  | 'infinite-paginated-api';

export type RunAndGetSchemaResponse = {
  response?: { [RAW_RESPONSE_KEY]: unknown; [key: string]: unknown };
  responseSchema?: {
    type: 'object';
    properties?: {
      [RAW_RESPONSE_KEY]?: JSONSchema7;
      [key: string]: JSONSchema7 | undefined;
    };
  };
};

const getExecuteWorkflowNodeMultipartQueryKey = (executeWorkflowNodeRequest: FormData) => {
  return ['/api/workflow/execute/node/multipart', executeWorkflowNodeRequest] as const;
};

export function createDataSourceDebugParams(debugParams: DebugParams) {
  return createSearchParams(omit(debugParams, 'meta'));
}

const getExecuteWorkflowAuthNodeQueryKey = (
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
) => {
  return ['/auth/workflow/execute/node', executeWorkflowNodeRequest] as const;
};

const getExecuteWorkflowNodeApiUpdatePasswordQueryKey = (
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
) => {
  return ['/api/user/update-password', executeWorkflowNodeRequest] as const;
};

const getExecuteWorkflowNodeRunGetSchemaQueryKey = (
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
) => {
  return ['/api/workflow/execute/node/rungetschema', executeWorkflowNodeRequest] as const;
};

const getExecuteWorkflowNodeInfiniteQueryKey = (
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
) => {
  return ['/api/workflow/execute/node', 'infinite', executeWorkflowNodeRequest] as const;
};

const getExecuteWorkflowNodeOfflineQueryKey = (
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
) => {
  return ['/offline/workflow/execute/node', executeWorkflowNodeRequest] as const;
};

const getExecuteWorkflowNodeSSEQueryKey = (
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
) => {
  return ['/api/workflow/execute/node/sse', executeWorkflowNodeRequest] as const;
};

const queryKeyMap: Record<
  ExecuteWorkflowNodeQueryKeyType,
  (
    executeWorkflowNodeRequest?: ExecuteWorkflowNodeRequest | FormData,
  ) =>
    | readonly [string, ExecuteWorkflowNodeRequest | FormData | undefined]
    | readonly [string, string, ExecuteWorkflowNodeRequest | FormData | undefined]
> = {
  offline: getExecuteWorkflowNodeOfflineQueryKey,
  sse: getExecuteWorkflowNodeSSEQueryKey,
  formdata: getExecuteWorkflowNodeMultipartQueryKey,
  auth: getExecuteWorkflowAuthNodeQueryKey,
  api: originalGetExecuteWorkflowNodeQueryKey,
  'api-update-password': getExecuteWorkflowNodeApiUpdatePasswordQueryKey,
  'api-rungetschema': getExecuteWorkflowNodeRunGetSchemaQueryKey,
  'infinite-paginated-api': getExecuteWorkflowNodeInfiniteQueryKey,
};

function getExecuteWorkflowNodeQueryKey(
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest | FormData,
  debugParams: DebugParams,
  queryKeyType: ExecuteWorkflowNodeQueryKeyType = 'api',
) {
  const originalQueryKey = queryKeyMap[queryKeyType](executeWorkflowNodeRequest);

  return [
    `${originalQueryKey[0]}?debugParams=${encodeURIComponent(JSON.stringify(debugParams))}`, // This is the only line that differs from the original function, which contains the url
    ...originalQueryKey.slice(1), // This is the input of the workflow node, and we are using it to show the InputTab make sure to update it if any changes are made here (apps/platform/src/modules/interfaces/views/InterfaceBuilder/components/ExplorerPanel/components/DataSources/components/ApplicationTypePropertiesPanel/components/DataSourcePreview/components/InputTab/InputTab.tsx)
  ] as const;
}

export const useExecuteWorkflowNodeQueryKeyMemo = createMemo(getExecuteWorkflowNodeQueryKey);

export function executeMultiPartFormDataWorkflowNode(
  executeWorkflowNodeRequest: FormData,
  debugParams: DebugParams,
) {
  const params = createDataSourceDebugParams(debugParams);

  return executeFetch<ExecuteWorkflowNodeResponse>(
    {
      url: `/api/workflow/execute/node/multipart?${params.toString()}`,
      method: 'POST',
      data: executeWorkflowNodeRequest,
    },
    { meta: debugParams?.meta },
  );
}

export function executeWorkflowNode(
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
  debugParams: DebugParams,
) {
  const params = createDataSourceDebugParams(debugParams);

  return executeFetch<ExecuteWorkflowNodeResponse>(
    {
      url: `/api/workflow/execute/node?${params.toString()}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: executeWorkflowNodeRequest,
    },
    { meta: debugParams?.meta },
  );
}

// server-owned names: a backend rename silently stops DPoP binding here
const PASSWORD_LOGIN_NODE = 'emailAndPassLoginRequest';
const VERIFY_MFA_NODE = 'verifyCode';

function appendDpopInputs(
  request: ExecuteWorkflowNodeRequest,
  publicKey: DpopPublicKey | null | undefined,
): ExecuteWorkflowNodeRequest {
  if (!publicKey) return request;
  return { ...request, inputs: appendDpopPublicKey(request.inputs ?? {}, publicKey) };
}

export async function executeAuthWorkflowNode(
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
  debugParams: DebugParams,
) {
  const searchParams = createDataSourceDebugParams(debugParams);
  const meta = debugParams?.meta;
  const nodeName = debugParams?.name;

  // login and MFA must agree on the backend, or MFA looks up a key that was never filed there
  const keyOrigin = getDomain(meta);

  // the only node that creates a key here; the platform auth pages bind their own
  if (nodeName === PASSWORD_LOGIN_NODE) {
    return withDpopLoginBinding(
      (dpopKeyPair) =>
        postAuthWorkflowNode(
          appendDpopInputs(executeWorkflowNodeRequest, dpopKeyPair?.publicKey),
          searchParams,
          { meta, dpopKeyPair },
        ),
      keyOrigin,
    );
  }

  const request =
    nodeName === VERIFY_MFA_NODE
      ? // reuse the key from login, never create a new one mid-MFA
        appendDpopInputs(executeWorkflowNodeRequest, await getDpopPublicKey(keyOrigin))
      : executeWorkflowNodeRequest;

  return postAuthWorkflowNode(request, searchParams, { meta });
}

export function executeUpdatePasswordWorkflowNode(
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
  debugParams: DebugParams,
) {
  const params = createDataSourceDebugParams(debugParams);

  return executeFetch<ExecuteWorkflowNodeResponse>(
    {
      url: `/api/user/update-password?${params.toString()}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: executeWorkflowNodeRequest,
    },
    { meta: debugParams?.meta },
  );
}

export function executeRunGetSchemaWorkflowNode(
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
  debugParams: DebugParams,
) {
  const params = createDataSourceDebugParams(debugParams);

  return executeFetch<ExecuteWorkflowNodeResponse>(
    {
      url: `/api/workflow/execute/node/rungetschema?${params.toString()}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: executeWorkflowNodeRequest,
    },
    { meta: debugParams?.meta },
  );
}

/**
 * Exchanges a temporary SSO token for a session cookie (_at).
 * On native, executeFetch auto-reads Set-Cookie and stores _at/_rt in MMKV via handleMobileCookies.
 * On web, the browser handles Set-Cookie natively.
 */
export function exchangeSSOToken(tempToken: string) {
  return executeFetch({
    url: `/auth/set-cookie-from-token?token=${encodeURIComponent(tempToken)}`,
    method: 'POST',
  });
}
